import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';
import { getCategoryPageData } from '../../../section/CMS/api/cmsApi';

import TextHeader from '../../textHeader/TextHeader';
import LoadingScreen from '../loading/LoadingScreen';
import MissingPage from '../errorMessages/MissingPage';
import DesignsInCollectionGrid from '../grid/DesignsInCollectionGrid';
import { captureAnalytics } from '../../../utilities/Analytics';
import { CMSPageData } from '../../../section/CMS/types/CMSTypes';

// Import new CMS components
import CMSConsumerCarouselBanner from '../../../section/CMS/components/banner/CMSConsumerCarouselBanner';
import CMSConsumerGallery from '../../../section/CMS/components/gallery/CMSConsumerGallery';

// Keep the old API imports for designs data (not part of CMS)
import { apiGetDesignsInCollection } from '../../../api/apiDesigns';

function CollectionTemplate() {
  const { item_group, collection } = useParams();
  const location = useLocation();

  // States for filters (keeping existing functionality)
  const [applicationsList, setApplicationsList] = useState<string[]>([]);
  const [colourList, setColourList] = useState<number[]>([]);
  const [designList, setDesignList] = useState<number[]>([]);
  const [typeList, setTypeList] = useState<number[]>([]);

  // CMS Data Query - Consumer always uses live mode, searches by collection name
  const {
    isSuccess: cmsSuccess,
    isError: cmsError,
    isLoading: cmsLoading,
    data: cmsResponse,
    refetch: cmsRefetch
  } = useQuery(
    [`cms-consumer-collection-${collection}`],
    () => getCategoryPageData(collection || '', 'live'), // Always live mode for consumer
    {
      staleTime: 600000,
      enabled: !!collection,
    }
  );

  // Get the CMS data (always use live data for consumer)
  const cmsData: CMSPageData | null = cmsResponse?.cms_page || null;

  // Keep existing designs API call (not part of CMS)
  const designFilters = {
    filters: {
      item_group: item_group,
      range: collection,
    },
    filter_fields: [
      "application",
      "item_group",
      "range",
      "brand",
      "web_colour",
      "design_style",
      "web_materials",
      "web_shape",
      "web_size",
      "web_type",
      "web_standards"
    ],
    main_filter_field: null,
    selection_fields: null,
    sort_by_setting: "Alphabetical A-Z",
    off_setter: {
      start: 0,
      end: 1000,
    },
    only_published: true,
    only_in_stock: false,
    limited: true,
  };

  const { data: designsData } = useQuery(
    [`collection-designs-${collection}-${item_group}`],
    () => apiGetDesignsInCollection(designFilters)
  );

  // URL parameter extraction (keeping existing functionality)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const applicationParams = searchParams.getAll('application');
    const colourParams = searchParams.getAll('colours');
    const designParams = searchParams.getAll('design_style');
    const typeParams = searchParams.getAll('types');

    setApplicationsList(applicationParams);
    setColourList(colourParams.map(Number));
    setDesignList(designParams.map(Number));
    setTypeList(typeParams.map(Number));
  }, [location.search]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Analytics tracking
  useEffect(() => {
    if (cmsSuccess && cmsData && collection) {
      const title = cmsData.page_title || `Collection - ${collection}`;
      captureAnalytics('pageview', location.pathname, title);
    }
  }, [location.pathname, collection, cmsSuccess, cmsData]);

  // Debug log for designs data (keeping existing functionality)
  useEffect(() => {
    if (designsData?.products) {
      const applications = Array.from(new Set(designsData.products.map((p: any) => p.application)));
      console.log('Applications found in collection:', applications);
      console.log('Total products:', designsData.products.length);
    }
  }, [designsData]);

  // Determine loading and error states
  const isLoading = cmsLoading;
  const isError = cmsError;
  const isSuccess = cmsSuccess;

  if (!collection) {
    return <MissingPage />;
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      {isError && <MissingPage />}
      {isSuccess && (
        <>
          {/* CMS Consumer Carousel Banner */}
          {cmsData && (
            <CMSConsumerCarouselBanner
              bannerMediaOption={cmsData.consumer_banner_media_option}
              bannerMediaCarousel={cmsData.consumer_banner_media_carousel || []}
              bannerMediaVideo={cmsData.consumer_banner_media_video}
              bannerMediaVideoLink={cmsData.consumer_banner_media_video_link}
              bannerLinkCategoryId={cmsData.consumer_banner_link_category_id}
              bannerWidth={cmsData.consumer_banner_width}
              bannerHeight={cmsData.consumer_banner_height}
              viewMode="live"
            />
          )}

          {/* Text Header */}
          <TextHeader
            heading={cmsData?.page_title || collection}
            content={cmsData?.description || ''}
            isCentered={false}
            width="70%"
            marginTop={10}
            marginBottom={5}
          />

          {/* CMS Consumer Gallery - 4 Image Layout */}
          {cmsData?.gallery_media && (
            <CMSConsumerGallery
              galleryMedia={cmsData.gallery_media}
              galleryLayout={'masonry'}
              columns={2}
              hasTitle={false}
              hasSecondaryText={false}
              marginTop={3}
              marginBottom={3}
            />
          )}

          {/* Designs in Collection Grid - Keep existing functionality (not part of CMS) */}
          <DesignsInCollectionGrid
            data={designsData}  // ✅ Pass the full object
            marginTop="5rem"
            marginBottom="5rem"
          />
        </>
      )}
    </>
  );
}

export default CollectionTemplate;